import { useLazyQuery, useMutation } from "@apollo/client";
import moment from "moment";
import { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, Row } from "reactstrap";
import { GET_SINGLE_TEMPLATE } from "../../Pages/settings/gql";
import { EmailModalInvoiceAttachmentProps } from "../../interfaces";
import EmailDraft from "../EmailDraft";
import CustomButton from "../layout/CustomButton";
import { EMAIL_MUTATION, GET_ALL_COMPANY_CONTACTS_EMAIL, UPDATE_SCHEDULED_EMAIL_MUTATION } from "./gql";
import { AppContext } from "../../context";
import { getInvoiceGreeting, maxDate } from "../../utils/helper";
import { outlookAccountingLink } from "../../utils/Temp-Helper";

const EmailModal: FC<EmailModalInvoiceAttachmentProps> = ({ invoicePreSelectEmail, companyId, placementId,
  isOpen, toggle, refetch, startDate, ccEmails, toEmail, accountManagerEmail, id, invoiceId, emailContent, fileName, source, editScheduledData }) => {

  const isEdit = !!editScheduledData
  const { user } = useContext(AppContext)
  const [invoiceScheduled, setInvoiceScheduled] = useState<boolean>(false)
  const [resendEmail, setResendEmail] = useState<boolean>(false)
  const [scheduledDate, setScheduledDate] = useState<string>('')
  const [emailMutation, { loading }] = useMutation(EMAIL_MUTATION, {
    onCompleted: () => {
      toast.success("email sent successfully")
      toggle()
      window.location.reload()
    }
  })
  const [updateScheduledMutation, { loading: updating }] = useMutation(UPDATE_SCHEDULED_EMAIL_MUTATION, {
    onCompleted: () => {
      toast.success("scheduled email updated successfully")
      toggle()
      window.location.reload()
    }
  })
  const [bodyContent, setBodyContent] = useState<string | null>();
  const [subject, setSubject] = useState<string | null>();
  const [companyContacts, setCompanyContacts] = useState<{ label: string, value: string }[]>([]);
  const [additionalFiles, setAdditionalFiles] = useState<{ source: string, fileName: string }[]>([]);

  const { register } = useForm()


  const [getTemplateQuery, { data }] = useLazyQuery(GET_SINGLE_TEMPLATE, {
    // onCompleted: () => console.log("data ", data),
    onError: (err) => toast.error(err?.message)
  })

  const [getAllContactsEmails] = useLazyQuery(GET_ALL_COMPANY_CONTACTS_EMAIL, {
    onCompleted: (data) => setCompanyContacts(data?.getAllCompanyContactsAndRecruitersEmails.filter((item, index, self) =>
      index === self.findIndex((e) => e.value === item.value)
    ))
  })

  const [mailContact, setMailContact] = useState<{ to: string[], ccContacts: string[]; bccContacts: string[] }>({
    to: [],
    ccContacts: [],
    bccContacts: [],
  });

  const [newMailContact, setNewMailContact] = useState<{ to: string[], ccContacts: string[]; bccContacts: string[] }>({
    to: [],
    ccContacts: [],
    bccContacts: [],
  });

  useEffect(() => {
    getTemplateQuery({
      variables: {
        module: resendEmail ? 'Invoice Aged Email' : 'Invoice Email'
      },
    })
    // setAgedInvoice(resendEmail)
  }, [resendEmail])

  useEffect(() => {
    if (companyId) {
      getAllContactsEmails({ variables: { companyId } })
    }
  }, [companyId])
  useEffect(() => {
    // Edit mode: pre-fill everything from the stored scheduled email instead of building a fresh draft.
    if (isOpen && isEdit && editScheduledData) {
      const { emailInput, scheduledDate: storedDate, files } = editScheduledData
      setMailContact({
        to: emailInput?.toContacts ?? [],
        ccContacts: emailInput?.ccContacts ?? [],
        bccContacts: emailInput?.bccContacts ?? [],
      })
      setSubject(emailInput?.subject ?? '')
      setBodyContent(emailInput?.bodyContent ?? '')
      setInvoiceScheduled(true)
      setScheduledDate(storedDate ? moment(Number(storedDate) || storedDate).format('YYYY-MM-DD') : '')
      setAdditionalFiles((files ?? []).slice(1))
      return
    }
    const preSelectCCEmails = invoicePreSelectEmail?.filter((item) => item?.emailType === "CC_EMAIL")?.map((item) => item?.email)
    const preSelectToEmails = invoicePreSelectEmail?.filter((item) => item?.emailType === "TO_EMAIL")?.map((item) => item?.email)
    const preSelectBCCEmails = invoicePreSelectEmail?.filter((item) => item?.emailType === "BCC_EMAIL")?.map((item) => item?.email)
    if (isOpen) {
      setMailContact({
        to: (Array.from(
          new Set([...preSelectToEmails, toEmail].filter(Boolean).reverse())
        ).reverse()),
        ccContacts: (Array.from(
          new Set([...preSelectCCEmails, ...ccEmails, "johnnyrenaudo@realrepp.com"]
            .filter(Boolean).reverse())
        ).reverse()),
        bccContacts: (Array.from(
          new Set([...preSelectBCCEmails, accountManagerEmail,].filter(Boolean).reverse())
        ).reverse())
      })
    } else {
      setSubject('')
      setBodyContent('')
    }
  }, [isOpen])

  useEffect(() => {
    const invoiceIdToUse = invoiceId ? invoiceId : '';
    // In edit mode the subject/body come from the stored scheduled email, not the template.
    if (data && !isEdit) {
      // Invoice Email / Invoice Aged Email templates should include {{greeting}} where the greeting should appear (scheduled => "Good morning", on-demand => time-based).
      setBodyContent(
        data?.getTemplateByModule?.template
          .replaceAll("{{greeting}}", getInvoiceGreeting(invoiceScheduled))
          .replaceAll("{{clientName}}", emailContent?.clientName)
          .replaceAll("{{candidateFullName}}", emailContent?.candidateFullName)
          .replaceAll("{{placementJobTitle}}", emailContent?.placementJobTitle)
          .replaceAll("{{jobCity}}", emailContent?.jobCity)
          .replaceAll("{{jobStateCode}}", emailContent?.jobStateCode)
          .replaceAll("{{invoiceId}}", invoiceIdToUse)
          .replaceAll("{{agedDays}}", moment().diff(moment(startDate), 'days'))

      )
      setSubject(data?.getTemplateByModule?.subject
        .replaceAll("{{invoiceId}}", invoiceIdToUse)
        .replaceAll("{{placementJobTitle}}", emailContent?.placementJobTitle)
        .replaceAll("{{candidateFullName}}", emailContent?.candidateFullName)
        .replaceAll("{{agedDays}}", moment().diff(moment(startDate), 'days'))
        .replaceAll(" - {{billBoxTag}}", emailContent?.billBoxTag ? ` - ${emailContent?.billBoxTag}` : '')
      )
    }
  }, [data, isOpen, invoiceScheduled])

  const handleSubmit = async () => {
    try {
      const input = {
        toContacts: mailContact.to,
        id: isEdit ? editScheduledData!.emailInput.id : id,
        bodyContent: bodyContent?.replaceAll('<p>', '').replaceAll('</p>', '<br>'),
        subject,
        ccContacts: mailContact.ccContacts,
        bccContacts: mailContact.bccContacts,
        toContactsNewCreated: newMailContact?.to,
        ccContactsNewCreated: newMailContact?.ccContacts,
        bccContactsNewCreated: newMailContact?.bccContacts,
        emailType: isEdit ? editScheduledData!.emailInput.emailType : (resendEmail ? 'Invoice Aged Email' : 'Invoice Email'),
      }
      if (isEdit) {
        await updateScheduledMutation({
          variables: {
            invoiceStatusId: editScheduledData!.invoiceStatusId,
            input,
            scheduledDate,
            additionalFiles
          }
        })
        refetch()
        return
      }
      await emailMutation({
        variables: {
          input,
          scheduledDate,
          placementId,
          additionalFiles
        }
      })
      refetch()
    } catch (error) {
      toast.error("something went wrong")
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="for-modal-styling">
      {user?.outLookAccountingMail ? <>
        <ModalBody>
          <div id="submittedEmail" className="writeup-wrap submission-package">
            <Card className="border-0">
              <CardBody className="p-0">
                <div className="dollar-addon-btn">
                  <div className="d-flex my-3 justify-content-between">
                    <Label className="text-14  ">Email</Label>
                    {user?.outLookAccountingToken && !invoiceScheduled &&
                      <div className="d-flex align-items-center">
                        <span className="switchLabel">Initial Sending</span>
                        <label className="switch m-0 mx-2">
                          <Input type="checkbox" onClick={(e) => setResendEmail(e.currentTarget.checked)} />
                          <span className="slider"></span>
                        </label>
                        <span className="switchLabel">Aged Invoice</span>
                      </div>}
                  </div>
                  <EmailDraft
                    fileName={fileName} source={source} setMailContact={setMailContact} setSubject={setSubject} setBodyContent={setBodyContent}
                    selectedFiles={additionalFiles} setSelectedFiles={setAdditionalFiles} setNewMailContact={setNewMailContact}
                    bodyContent={bodyContent} mailContact={mailContact} subject={subject} newMailContact={newMailContact}
                    companyContacts={companyContacts}
                  />
                </div>
              </CardBody>
            </Card>
          </div>

          {!resendEmail && <>
            {!isEdit && <div className="d-flex align-items-center my-3 ml-0">
              <label className="switch m-0 mx-2">
                <Input type="checkbox" onClick={(e) => setInvoiceScheduled(e.currentTarget.checked)} />
                <span className="slider"></span>
              </label>
              <span className="switchLabel">Schedule Invoice Email</span>
            </div>}

            {(invoiceScheduled || isEdit) && <Row>
              <Col xs="6" className="mb-3">
                <div className="custom-amount-form-input">
                  <Input
                    min={maxDate()}
                    value={scheduledDate}
                    placeholder="Received Date"
                    type="date"
                    onChange={({ target: { value } }) => {
                      setScheduledDate(value)
                    }} />
                </div>
              </Col >
            </Row>}
          </>}
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={toggle} className="buttonPrimary mr-3">
            Cancel
          </button>
          <CustomButton
            loading={loading || updating}
            disabled={loading || updating || !mailContact?.to?.length || (invoiceScheduled && !scheduledDate)}
            buttonClick={handleSubmit}
            buttonText={isEdit ? "Update Scheduled Email" : (invoiceScheduled ? "Schedule Email" : "Send Email")}
          />
        </ModalFooter>
      </> :
        <Card className="border-0">
          <CardBody className="p-0">
            <div className="d-flex px-3 py-5 border">
              <span>
                Signin with your <strong> Accounting Outlook </strong> account
                first!
              </span>
              {outlookAccountingLink}
            </div>
          </CardBody>
        </Card>}
    </Modal>)

}
export default EmailModal